import { useState, useEffect, createContext, useContext, ReactNode } from 'react';

type Theme = 'light' | 'dark' | 'system';
type AccentColor = 'coral' | 'blue' | 'green' | 'orange' | 'pink' | 'red';

interface ThemeContextType {
  theme: Theme;
  accentColor: AccentColor;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  setAccentColor: (color: AccentColor) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const ACCENT_COLORS: Record<AccentColor, { primary: string; primaryForeground: string }> = {
  coral: { primary: '15 76% 60%', primaryForeground: '0 0% 100%' },
  blue: { primary: '217.2 91.2% 59.8%', primaryForeground: '210 20% 98%' },
  green: { primary: '142.1 76.2% 36.3%', primaryForeground: '355.7 100% 97.3%' },
  orange: { primary: '24.6 95% 53.1%', primaryForeground: '60 9.1% 97.8%' },
  pink: { primary: '330.4 81.2% 60.4%', primaryForeground: '210 20% 98%' },
  red: { primary: '0 72.2% 50.6%', primaryForeground: '0 0% 98%' },
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>('system');
  const [accentColor, setAccentColorState] = useState<AccentColor>('coral');
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    // Load saved preferences
    const savedTheme = localStorage.getItem('theme') as Theme;
    const savedAccent = localStorage.getItem('accentColor') as AccentColor;

    if (savedTheme) setThemeState(savedTheme);
    if (savedAccent) setAccentColorState(savedAccent);
  }, []);

  useEffect(() => {
    // Apply theme
    const root = document.documentElement;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const applyTheme = () => {
      let resolved: 'light' | 'dark';
      if (theme === 'system') {
        resolved = mediaQuery.matches ? 'dark' : 'light';
      } else {
        resolved = theme;
      }

      setResolvedTheme(resolved);

      if (resolved === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };

    applyTheme();
    mediaQuery.addEventListener('change', applyTheme);

    return () => mediaQuery.removeEventListener('change', applyTheme);
  }, [theme]);

  useEffect(() => {
    // Apply accent color
    const root = document.documentElement;
    const colors = ACCENT_COLORS[accentColor];
    root.style.setProperty('--primary', colors.primary);
    root.style.setProperty('--primary-foreground', colors.primaryForeground);
  }, [accentColor]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const setAccentColor = (color: AccentColor) => {
    setAccentColorState(color);
    localStorage.setItem('accentColor', color);
  };

  return (
    <ThemeContext.Provider
      value={{ theme, accentColor, resolvedTheme, setTheme, setAccentColor }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);

  // Fallback for when ThemeProvider is not available (backwards compatibility)
  if (context === undefined) {
    // Simple theme toggle without context
    const getTheme = (): 'light' | 'dark' => {
      if (typeof window === 'undefined') return 'light';
      const stored = localStorage.getItem('theme');
      if (stored === 'dark' || stored === 'light') return stored;
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    };

    const toggleTheme = () => {
      const current = getTheme();
      const next = current === 'dark' ? 'light' : 'dark';
      localStorage.setItem('theme', next);
      document.documentElement.classList.toggle('dark', next === 'dark');
      window.location.reload(); // Simple reload to apply
    };

    return {
      theme: getTheme(),
      toggleTheme,
      resolvedTheme: getTheme(),
      accentColor: 'purple' as const,
      setTheme: (t: Theme) => {
        localStorage.setItem('theme', t);
        document.documentElement.classList.toggle('dark', t === 'dark');
      },
      setAccentColor: () => {},
    };
  }

  // Add toggleTheme for backwards compatibility
  const toggleTheme = () => {
    const next = context.resolvedTheme === 'dark' ? 'light' : 'dark';
    context.setTheme(next);
  };

  return { ...context, toggleTheme };
}

export const accentColorOptions: AccentColor[] = ['coral', 'blue', 'green', 'orange', 'pink', 'red'];
